// Broker Channel Batch (WS6) — broker teams + graduation. Mirrors the
// backend BrokerTeamsController (api/broker-teams): reads gate on
// Brokers.Read, mutations on Brokers.Update. Assigning a member graduates
// a Certified broker onto the team (a broker already on another team is
// moved in the same action).

import { api } from "./client";
import type { BrokerStatus } from "./brokers";

export type BrokerTeamStatus = "Open" | "Archived";

export type DealPipelineStage =
  | "Intake" | "PreApproval" | "Underwriting" | "Closing"
  | "Closed" | "Declined" | "Withdrawn";

export interface BrokerTeamSummaryDto {
  id: string;
  name: string;
  description?: string | null;
  ownerUserId?: string | null;
  ownerDisplayName?: string | null;
  status: BrokerTeamStatus;
  createdAt: string;
  brokerCount: number;
}

export interface CreateBrokerTeamRequest {
  name: string;
  description?: string | null;
  ownerUserId?: string | null;
}

/** FIND-12 semantics: null/omitted = unchanged; an empty string clears
 *  description / ownerUserId. Name cannot be cleared, only renamed. */
export interface UpdateBrokerTeamRequest {
  name?: string | null;
  description?: string | null;
  ownerUserId?: string | null;
}

export interface BrokerTeamMemberDto {
  brokerId: string;
  companyName: string;
  name?: string | null;
  status: BrokerStatus;
  graduatedAt?: string | null;
  dealCount: number;
}

/** One pipeline-stage bucket of the team's deals (all 7 stages are always
 *  emitted, zero counts included). */
export interface BrokerTeamPipelineMixDto {
  stage: DealPipelineStage;
  count: number;
}

export interface BrokerTeamDashboardDto {
  id: string;
  name: string;
  description?: string | null;
  ownerUserId?: string | null;
  ownerDisplayName?: string | null;
  status: BrokerTeamStatus;
  createdAt: string;
  brokerCount: number;
  dealsSourcedCount: number;
  dealsSourcedVolume: number;
  loansOriginatedCount: number;
  loansOriginatedTotal: number;
  activeDealCount: number;
  closedDealCount: number;
  avgDealsPerBroker: number;
  pipelineMix: BrokerTeamPipelineMixDto[];
  members: BrokerTeamMemberDto[];
}

export const brokerTeamsApi = {
  list: (includeArchived = false) =>
    api.get<BrokerTeamSummaryDto[]>("/broker-teams", { includeArchived }),
  create: (body: CreateBrokerTeamRequest) =>
    api.post<BrokerTeamSummaryDto>("/broker-teams", body),
  update: (id: string, body: UpdateBrokerTeamRequest) =>
    api.patch<BrokerTeamSummaryDto>(`/broker-teams/${id}`, body),
  archive: (id: string) =>
    api.post<BrokerTeamSummaryDto>(`/broker-teams/${id}/archive`),
  unarchive: (id: string) =>
    api.post<BrokerTeamSummaryDto>(`/broker-teams/${id}/unarchive`),
  dashboard: (id: string) =>
    api.get<BrokerTeamDashboardDto>(`/broker-teams/${id}/dashboard`),
  assignMember: (id: string, brokerId: string) =>
    api.post<boolean>(`/broker-teams/${id}/members`, { brokerId }),
  removeMember: (id: string, brokerId: string) =>
    api.delete<boolean>(`/broker-teams/${id}/members/${brokerId}`),
};
